import { getApiError } from 'src/api/utils';
import {
	addCriticalBreadcrumb,
	captureCriticalError,
	formatSentryError,
	shouldCaptureHandledError,
} from 'src/modules/sentry-observability';
import {
	getSentryEventCreationState,
	type SentryEventCreationFlow,
	type SentryEventCreationStep,
} from 'src/modules/sentry-event-creation-state';
import type { SentryEventCreationErrorDomain } from 'src/modules/sentry-taxonomy';

export type EventCreationFailureKind =
	| 'terminal_api'
	| 'partial_success'
	| 'invariant';

export type EventCreationFailureDecision = 'issue' | 'breadcrumb';

export type EventCreationHandledReason =
	| 'validation'
	| 'stale'
	| 'blocked'
	| 'conflict'
	| 'auth_pending'
	| 'cancelled';

export type EventCreationNavigationOutcome =
	| 'step_entered'
	| 'step_ready'
	| 'next_attempt'
	| 'back_attempt'
	| 'waiting_for_sync'
	| 'validation_blocked'
	| 'missing_required_state'
	| 'navigation_dispatched'
	| 'navigation_succeeded'
	| 'navigation_blocked'
	| 'stalled'
	| 'recovered';

export type EventCreationBlockingReason =
	| 'category_missing'
	| 'banner_invalid'
	| 'resource_id_missing'
	| 'name_sync_pending'
	| 'category_sync_pending'
	| 'banner_sync_pending'
	| 'session_create_pending'
	| 'session_update_pending'
	| 'session_failure_residual'
	| 'place_link_pending'
	| 'publish_pending'
	| 'route_not_changed'
	| 'unknown';

export type EventCreationBannerState =
	| 'absent'
	| 'remote'
	| 'local'
	| 'oversized';

export type EventCreationNavigationContext = {
	invalidFields?: string[];
	pendingOperations?: number;
	pendingOperationTypes?: string[];
	failedSyncCount?: number;
	elapsedMs?: number;
	online?: boolean;
	documentVisible?: boolean;
	formValid?: boolean;
	routeChanged?: boolean;
	bannerState?: EventCreationBannerState;
	[key: string]: unknown;
};

export type EventCreationNavigationParams = {
	action: string;
	outcome: EventCreationNavigationOutcome;
	step?: SentryEventCreationStep;
	flow?: SentryEventCreationFlow;
	blockingReason?: EventCreationBlockingReason;
	context?: EventCreationNavigationContext;
	error?: unknown;
};

type EventCreationFailureDetails = {
	error: unknown;
	action: string;
	errorDomain: SentryEventCreationErrorDomain;
	failureKind?: EventCreationFailureKind;
	flow?: SentryEventCreationFlow;
	step?: SentryEventCreationStep;
	handledReason?: EventCreationHandledReason;
	context?: Record<string, boolean | number | string | null | undefined>;
};

const reportedErrors = new WeakSet<object>();

const getErrorStatus = (error: unknown): number | undefined => {
	const apiStatus = getApiError(error)?.status;
	if (typeof apiStatus === 'number') return apiStatus;

	const candidate = error as {
		status?: number;
		response?: { status?: number };
	};
	return candidate?.response?.status ?? candidate?.status;
};

const isOffline = () =>
	typeof navigator !== 'undefined' && navigator.onLine === false;

export const getEventCreationFailureDecision = ({
	failureKind = 'terminal_api',
	handledReason,
}: Pick<
	EventCreationFailureDetails,
	'failureKind' | 'handledReason'
>): EventCreationFailureDecision => {
	if (failureKind === 'partial_success' || failureKind === 'invariant') {
		return 'issue';
	}
	if (isOffline() || handledReason) return 'breadcrumb';
	return 'issue';
};

const ACTION_CONTEXT_MAP: Record<string, string> = {
	// Bootstrap
	create_draft_failed: 'Bootstrap - Criar rascunho',
	create_draft: 'Bootstrap - Criar rascunho',
	bootstrap_draft: 'Bootstrap - Criar rascunho',
	continue_draft_failed: 'Bootstrap - Continuar rascunho',
	continue_draft: 'Bootstrap - Continuar rascunho',
	load_draft_failed: 'Bootstrap - Carregar rascunho',
	load_draft: 'Bootstrap - Carregar rascunho',
	list_drafts: 'Bootstrap - Listar rascunhos',

	// Edição
	load_event_failed: 'Edição - Carregar evento',
	load_event: 'Edição - Carregar evento',
	create_edit_draft_failed: 'Edição - Criar rascunho',
	create_edit_draft: 'Edição - Criar rascunho',
	restart_stale_edit_failed: 'Edição - Recuperar informações desatualizadas',

	// Dados básicos
	save_name_failed: 'Dados básicos - Salvar nome',
	update_name: 'Dados básicos - Salvar nome',
	save_category_failed: 'Dados básicos - Salvar categoria',
	update_category: 'Dados básicos - Salvar categoria',
	load_categories: 'Dados básicos - Carregar categorias',
	load_categories_failed: 'Dados básicos - Carregar categorias',
	save_visibility_failed: 'Dados básicos - Salvar visibilidade',
	update_visibility: 'Dados básicos - Salvar visibilidade',
	save_description_failed: 'Dados básicos - Salvar descrição',
	update_description: 'Dados básicos - Salvar descrição',
	update_draft_failed: 'Dados básicos - Salvar rascunho',
	update_draft: 'Dados básicos - Salvar rascunho',

	// Banner
	upload_banner_failed: 'Banner - Enviar',
	upload_banner: 'Banner - Enviar',
	delete_banner_failed: 'Banner - Remover',
	delete_banner: 'Banner - Remover',

	// Local
	save_place_failed: 'Local - Salvar',
	create_place: 'Local - Salvar',
	create_place_failed: 'Local - Salvar',
	update_place_failed: 'Local - Salvar',
	update_place: 'Local - Salvar',
	link_place_failed: 'Local - Vincular',
	link_place: 'Local - Vincular',
	unlink_place_failed: 'Local - Desvincular',
	unlink_place: 'Local - Desvincular',

	// Sessões
	load_sessions_failed: 'Sessões - Carregar',
	load_sessions: 'Sessões - Carregar',
	create_session_failed: 'Sessões - Criar',
	create_session: 'Sessões - Criar',
	update_session_failed: 'Sessões - Atualizar',
	update_session: 'Sessões - Atualizar',
	delete_session_failed: 'Sessões - Remover',
	delete_session: 'Sessões - Remover',

	// Ingressos
	hydrate_tickets_failed: 'Ingressos - Carregar',
	load_tickets_failed: 'Ingressos - Carregar',
	load_tickets: 'Ingressos - Carregar',
	load_ticket_defaults: 'Ingressos - Carregar configurações padrão',
	create_ticket_failed: 'Ingressos - Salvar',
	save_ticket_failed: 'Ingressos - Salvar',
	save_ticket: 'Ingressos - Salvar',
	create_ticket: 'Ingressos - Salvar',
	update_ticket_failed: 'Ingressos - Atualizar',
	update_ticket: 'Ingressos - Atualizar',
	delete_ticket_failed: 'Ingressos - Remover',
	delete_ticket: 'Ingressos - Remover',
	remove_ticket_from_group_failed: 'Ingressos - Remover do grupo',
	persist_ticket_hierarchy_failed: 'Ingressos - Salvar posição dos grupos',
	close_ticket_failed: 'Ingressos - Encerrar',
	close_ticket: 'Ingressos - Encerrar',
	resume_ticket_failed: 'Ingressos - Reativar',
	resume_ticket: 'Ingressos - Reativar',
	reconcile_tickets_failed: 'Ingressos - Reconciliar',

	// Virada de lote
	batch_turnover_partial_failure: 'Ingressos - Virada de lote',
	batch_turnover_invalid_chain: 'Ingressos - Virada de lote',

	// Ordenação
	sort_tickets_failed: 'Ingressos - Ordenar',
	sort_tickets: 'Ingressos - Ordenar',
	sort_tickets_invalid_response: 'Ingressos - Ordenação inválida',

	// Taxa
	update_tax_failed: 'Taxa - Atualizar',
	update_tax: 'Taxa - Atualizar',

	// Publicação
	publish_create_failed: 'Publicação - Criar evento',
	publish_event: 'Publicação - Criar evento',
	publish_draft: 'Publicação - Criar evento',
	publish_edit_failed: 'Publicação - Salvar edição',
	publish_upload_banner_failed: 'Publicação - Enviar banner',
	publish_missing_draft: 'Publicação - Rascunho ausente',

	// Autenticação
	resume_after_auth_wrong_draft: 'Autenticação - Retomar rascunho',
	resume_after_auth_failed: 'Autenticação - Retomar rascunho',

	// Invariante
	missing_mandatory_state: 'Invariante - Estado obrigatório ausente',
	incompatible_response: 'Invariante - Resposta incompatível',
};

const STEP_FALLBACK_MAP: Record<SentryEventCreationStep, string> = {
	event_start: 'Bootstrap',
	event_bootstrap: 'Bootstrap',
	event_what: 'Dados básicos',
	event_when: 'Sessões',
	event_where: 'Local',
	event_how_much: 'Ingressos',
	event_publish: 'Publicação',
};

export const getEventCreationActionLabel = (
	action: string,
	step?: SentryEventCreationStep
): string => {
	if (ACTION_CONTEXT_MAP[action]) {
		return ACTION_CONTEXT_MAP[action];
	}
	if (step && STEP_FALLBACK_MAP[step]) {
		return STEP_FALLBACK_MAP[step];
	}
	return 'Criar Evento';
};

export const reportEventCreationFailure = ({
	error,
	action,
	errorDomain,
	failureKind = 'terminal_api',
	flow,
	step,
	handledReason,
	context,
}: EventCreationFailureDetails): EventCreationFailureDecision => {
	const state = getSentryEventCreationState();
	const resolvedFlow = flow ?? state?.flow ?? 'create_event';
	const resolvedStep = step ?? state?.step ?? 'event_what';
	const status = getErrorStatus(error);
	const errorCode = getApiError(error)?.code;
	const decision = getEventCreationFailureDecision({
		failureKind,
		handledReason,
	});
	const safeContext = {
		failureKind,
		handledReason: handledReason ?? null,
		status: status ?? null,
		errorCode: errorCode ?? null,
		offline: isOffline(),
		...context,
	};

	addCriticalBreadcrumb({
		area: 'event_creation',
		flow: resolvedFlow,
		step: resolvedStep,
		action: `${action}_${decision}`,
		level: decision === 'issue' ? 'error' : 'warning',
		data: safeContext,
	});

	const wasReported =
		typeof error === 'object' && error !== null && reportedErrors.has(error);

	if (decision === 'issue' && !wasReported && shouldCaptureHandledError(error)) {
		const formattedError = formatSentryError(error, {
			name: 'Criar Evento',
			context: getEventCreationActionLabel(action, resolvedStep),
		});

		captureCriticalError(formattedError, {
			area: 'event_creation',
			flow: resolvedFlow,
			step: resolvedStep,
			source: failureKind === 'terminal_api' ? 'api' : 'state',
			context: safeContext,
			tags: {
				event_creation_action: action,
				error_domain: errorDomain,
				failure_kind: failureKind,
				http_status: status ? String(status) : 'network_error',
				api_error_code: errorCode,
			},
			fingerprint: [
				'event-creation',
				resolvedFlow,
				action,
				failureKind,
				String(status ?? 'network_error'),
				errorCode ?? 'no-code',
			],
		});
		if (typeof error === 'object' && error !== null) {
			reportedErrors.add(error);
		}
	}

	return decision;
};

export const NAVIGATION_STEP_CONTEXT_MAP: Record<SentryEventCreationStep, string> = {
	event_start: 'Início - Continuar',
	event_bootstrap: 'Bootstrap - Preparar fluxo',
	event_what: 'Dados básicos - Avançar',
	event_when: 'Sessões - Avançar',
	event_where: 'Local - Avançar',
	event_how_much: 'Ingressos - Salvar',
	event_publish: 'Publicação - Concluir',
};

const NAVIGATION_ISSUE_OUTCOMES = new Set<EventCreationNavigationOutcome>([
	'missing_required_state',
	'navigation_blocked',
	'stalled',
]);

export const isNavigationIssueOutcome = (
	outcome: EventCreationNavigationOutcome
): boolean => NAVIGATION_ISSUE_OUTCOMES.has(outcome);

export const sanitizeNavigationContext = (
	context?: EventCreationNavigationContext
): Record<string, unknown> => {
	const safe: Record<string, unknown> = {};

	if (Array.isArray(context?.invalidFields)) {
		safe.invalidFields = context!.invalidFields
			.filter((f): f is string => typeof f === 'string')
			.slice(0, 20);
	}
	if (typeof context?.pendingOperations === 'number') {
		safe.pendingOperations = context.pendingOperations;
	}
	if (Array.isArray(context?.pendingOperationTypes)) {
		safe.pendingOperationTypes = context!.pendingOperationTypes
			.filter((t): t is string => typeof t === 'string')
			.slice(0, 20);
	}
	if (typeof context?.failedSyncCount === 'number') {
		safe.failedSyncCount = context.failedSyncCount;
	}
	if (typeof context?.elapsedMs === 'number') {
		safe.elapsedMs = context.elapsedMs;
	}
	safe.online =
		typeof context?.online === 'boolean'
			? context.online
			: !isOffline();
	safe.documentVisible =
		typeof context?.documentVisible === 'boolean'
			? context.documentVisible
			: typeof document !== 'undefined'
				? document.visibilityState === 'visible'
				: true;
	if (typeof context?.formValid === 'boolean') {
		safe.formValid = context.formValid;
	}
	if (typeof context?.routeChanged === 'boolean') {
		safe.routeChanged = context.routeChanged;
	}
	if (
		context?.bannerState === 'absent' ||
		context?.bannerState === 'remote' ||
		context?.bannerState === 'local' ||
		context?.bannerState === 'oversized'
	) {
		safe.bannerState = context.bannerState;
	}

	return safe;
};

export const resolveEventCreationBannerState = (
	banner: unknown
): EventCreationBannerState => {
	if (!banner || typeof banner !== 'object') return 'absent';
	const b = banner as {
		file?: unknown;
		cropped?: unknown;
		size?: unknown;
		isOversized?: boolean;
	};
	if (
		b.isOversized ||
		(typeof b.size === 'number' && b.size > 2097152)
	) {
		return 'oversized';
	}
	if (
		(typeof File !== 'undefined' && b.file instanceof File) ||
		(typeof Blob !== 'undefined' && b.cropped instanceof Blob)
	) {
		return 'local';
	}
	if (typeof b.file === 'string' && b.file.length > 0) {
		return 'remote';
	}
	return 'absent';
};

export const reportEventCreationNavigation = ({
	action,
	outcome,
	step,
	flow,
	blockingReason,
	context,
	error,
}: EventCreationNavigationParams): void => {
	const state = getSentryEventCreationState();
	const resolvedFlow = flow ?? state?.flow ?? 'create_event';
	const resolvedStep = step ?? state?.step ?? 'event_what';
	const safeContext = sanitizeNavigationContext(context);
	const isIssue = isNavigationIssueOutcome(outcome);

	const breadcrumbLevel: 'info' | 'warning' | 'error' = isIssue
		? 'error'
		: outcome === 'validation_blocked'
			? 'warning'
			: 'info';

	addCriticalBreadcrumb({
		area: 'event_creation',
		flow: resolvedFlow,
		step: resolvedStep,
		action,
		level: breadcrumbLevel,
		data: {
			outcome,
			blockingReason: blockingReason ?? null,
			...safeContext,
		},
	});

	if (isIssue) {
		const defaultErrorMessage =
			outcome === 'stalled'
				? 'Tentativa pendente por mais de 15 segundos'
				: outcome === 'missing_required_state'
					? 'Estado obrigatório ausente'
					: 'Navegação bloqueada';

		const baseError =
			error instanceof Error
				? error
				: typeof error === 'string'
					? new Error(error)
					: new Error(defaultErrorMessage);

		const formattedError = formatSentryError(baseError, {
			name: 'Criar Evento',
			context: NAVIGATION_STEP_CONTEXT_MAP[resolvedStep] ?? 'Criar Evento',
		});

		captureCriticalError(formattedError, {
			area: 'event_creation',
			flow: resolvedFlow,
			step: resolvedStep,
			source: 'navigation',
			level: 'error',
			context: safeContext,
			tags: {
				event_creation_action: action,
				navigation_outcome: outcome,
				...(blockingReason ? { blocking_reason: blockingReason } : {}),
				flow_version: 'new',
			},
			fingerprint: [
				'event-creation',
				resolvedFlow,
				resolvedStep,
				outcome,
				blockingReason ?? 'unknown',
			],
		});
	}
};

type PendingNavigation = {
	sourceStep: SentryEventCreationStep;
	targetStep?: SentryEventCreationStep | string;
	action: string;
	flow: SentryEventCreationFlow;
	dispatchedAt: number;
	timeoutId: ReturnType<typeof setTimeout> | null;
	context?: EventCreationNavigationContext;
};

let activePendingNavigation: PendingNavigation | null = null;
let lastAttemptInfo: { step: string; action: string; time: number } | null = null;

export const isDuplicateNavigationAttempt = (
	action: string,
	step: string,
	thresholdMs = 500
): boolean => {
	const now = Date.now();
	if (
		lastAttemptInfo &&
		lastAttemptInfo.step === step &&
		lastAttemptInfo.action === action &&
		now - lastAttemptInfo.time < thresholdMs
	) {
		return true;
	}
	lastAttemptInfo = { step, action, time: now };
	return false;
};

export const startNavigationWatchdog = ({
	sourceStep,
	targetStep,
	action = 'next_attempt',
	flow,
	context,
	timeoutMs = 15000,
}: {
	sourceStep: SentryEventCreationStep;
	targetStep?: SentryEventCreationStep | string;
	action?: string;
	flow?: SentryEventCreationFlow;
	context?: EventCreationNavigationContext;
	timeoutMs?: number;
}) => {
	if (activePendingNavigation?.timeoutId) {
		clearTimeout(activePendingNavigation.timeoutId);
	}

	const state = getSentryEventCreationState();
	const resolvedFlow = flow ?? state?.flow ?? 'create_event';
	const dispatchedAt = Date.now();

	const timeoutId = setTimeout(() => {
		if (activePendingNavigation) {
			reportEventCreationNavigation({
				action,
				outcome: 'stalled',
				step: sourceStep,
				flow: resolvedFlow,
				blockingReason: 'route_not_changed',
				context: {
					...context,
					elapsedMs: Date.now() - dispatchedAt,
					routeChanged: false,
				},
				error: new Error('Tentativa pendente por mais de 15 segundos'),
			});
			activePendingNavigation = null;
		}
	}, timeoutMs);

	activePendingNavigation = {
		sourceStep,
		targetStep,
		action,
		flow: resolvedFlow,
		dispatchedAt,
		timeoutId,
		context,
	};
};

export const completeNavigationWatchdog = (
	mountedStep?: SentryEventCreationStep | string
) => {
	if (!activePendingNavigation) return;
	if (mountedStep && activePendingNavigation.sourceStep === mountedStep) {
		return;
	}

	const pending = activePendingNavigation;
	if (pending.timeoutId) {
		clearTimeout(pending.timeoutId);
	}
	activePendingNavigation = null;

	const elapsedMs = Date.now() - pending.dispatchedAt;

	reportEventCreationNavigation({
		action: pending.action,
		outcome: 'navigation_succeeded',
		step: pending.sourceStep,
		flow: pending.flow,
		context: {
			...pending.context,
			elapsedMs,
			routeChanged: true,
		},
	});
};

export const cancelNavigationWatchdog = (
	stepToCancel?: SentryEventCreationStep
) => {
	if (
		activePendingNavigation?.timeoutId &&
		(!stepToCancel || activePendingNavigation.sourceStep === stepToCancel)
	) {
		clearTimeout(activePendingNavigation.timeoutId);
		activePendingNavigation = null;
	}
};

export const resetNavigationWatchdogForTesting = () => {
	cancelNavigationWatchdog();
	lastAttemptInfo = null;
};

